import React, { useCallback, useEffect, useRef, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { CountDetail, CountPending, CountSummary } from "../../../../services/v2/stockops.service";
import { ProductRow } from "../../../../services/v2/inventory.service";
import ReasonPrompt from "../../../../components/kits/reasonPrompt";
import ProductPicker from "../products/productPicker";
import { Empty, Loading, apiError, openBlob, ui } from "../kits/shared";
import { Modal, PhotoInput } from "./traslados";
import Escaner, { CantidadesPorDarDeAlta, NombrePorDarDeAlta, NuevoProducto, currencyOf, pendingGroups } from "./escaner";
import ContarPorTallas from "./contarPorTallas";
import HistorialConteo from "./historialConteo";
import { push } from "../../sales/push";

/*
	Bodegas → Conteos (sección "stockCounts"). Contando (a ciegas) → Por firmar (otra persona revisa y firma) →
	Por aprobar → Aplicado (Super Admin; la diferencia entra como saldo inicial o ajuste, con costo de arranque opcional).
*/

const S = { COUNTING: 1, TO_SIGN: 2, TO_APPROVE: 3, APPLIED: 4, CANCELLED: 5 };

const chip = (statusId: number, text: string) => {
	const style = statusId === S.APPLIED ? { color: "var(--wa-verde)", background: "var(--wa-verde-suave)" }
		: statusId === S.CANCELLED ? { color: "var(--wa-apagado)", background: "var(--wa-papel-2)" }
		: statusId === S.TO_APPROVE ? { color: "var(--wa-acento-texto)", background: "var(--wa-acento-suave)" }
		: { color: "var(--wa-tinta)", background: "var(--wa-papel-3)" };
	return <span style={{ ...style, borderRadius: "10px", padding: "1px 9px", fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap" }}>{text}</span>;
};

const Conteos: React.FC<{ campusId: number; warehouses: any[]; perms: any; idleLocked: boolean }> = ({ campusId, warehouses, perms, idleLocked }) => {
	const http = HttpManager.getInstance().stockOpsService;
	const [rows, setRows] = useState<CountSummary[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [openId, setOpenId] = useState<number | null>(null);
	const [creating, setCreating] = useState<boolean>(false);

	const load = useCallback(() => {
		http.counts(campusId).then((response) => { setError(null); setRows(response.data); }).catch((requestError) => { setRows([]); setError(apiError(requestError, "No se pudieron cargar los conteos.")); });
	}, [campusId]);
	useEffect(() => { load(); }, [load]);

	return <>
		{perms.create ? <div style={{ display: "flex", justifyContent: "flex-end", margin: "0 0 10px" }}>
			<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={idleLocked} onClick={() => setCreating(true)}>Nuevo Conteo</button>
		</div> : null}
		<div className="waScrollX" style={{ ...ui.card, overflowX: "auto" }}>
			{rows === null ? <Loading /> : error ? <div className="alert alert-danger" style={{ margin: "16px" }}>{error}</div>
				: rows.length < 1 ? <Empty text="Sin conteos. El conteo inicial de cada bodega es lo primero." />
				: <table style={{ width: "100%", borderCollapse: "collapse" }}>
					<thead><tr>
						<th style={ui.th}>Conteo</th>
						<th style={ui.th}>Bodega</th>
						<th style={{ ...ui.th, ...ui.right }}>Productos</th>
						<th style={{ ...ui.th, ...ui.right }}>Piezas</th>
						<th style={ui.th}>Contó</th>
						<th style={ui.th}>Firmó</th>
						<th style={ui.th}>Estado</th>
					</tr></thead>
					<tbody>{rows.map((row) => <tr key={row.id} style={{ cursor: "pointer" }} onClick={() => setOpenId(row.id)}>
						<td style={ui.td}><div style={{ fontFamily: "monospace", fontWeight: 700 }}>{row.folio}</div><div style={ui.tiny}>{row.kindLabel} · {row.createdAt}</div>
							<button type="button" className={"btn btn-sm rounded-pill " + (row.statusId === S.COUNTING ? "btn-primary" : "btn-outline-secondary")} style={{ marginTop: "4px", whiteSpace: "nowrap" }}
								onClick={(event) => { event.stopPropagation(); setOpenId(row.id); }}>{row.statusId === S.COUNTING ? "Seguir Contando" : "Abrir"}</button></td>
						<td style={ui.td}>{row.warehouse}{row.shelves ? <div style={ui.tiny}>{row.shelves}</div> : null}</td>
						<td style={{ ...ui.td, ...ui.right }}>{row.lineCount}</td>
						<td style={{ ...ui.td, ...ui.right }}>{row.pieces}</td>
						<td style={ui.td}>{row.countedBy}</td>
						<td style={ui.td}>{row.secondCounter || "-"}{row.signedAt ? <div style={ui.tiny}>{row.signedAt}</div> : null}</td>
						<td style={ui.td}>{chip(row.statusId, row.status)}{row.approvedBy ? <div style={ui.tiny}>aplicó {row.approvedBy}</div> : null}
								{row.pendingCount > 0 ? <div style={{ fontSize: "12px", fontWeight: 700, color: "var(--wa-oro)", marginTop: "3px", whiteSpace: "nowrap" }}>{row.pendingCount} por dar de alta</div> : null}</td>
					</tr>)}</tbody>
				</table>}
		</div>
		{creating ? <NewCount campusId={campusId} warehouses={warehouses} rows={rows || []} onOpen={(id) => { setCreating(false); setOpenId(id); }} onCancel={() => setCreating(false)} onDone={(count) => { setCreating(false); load(); setOpenId(count.id); }} /> : null}
		{openId !== null ? <CountModal campusId={campusId} countId={openId} canCount={!!perms.create} onChanged={load} onClose={() => setOpenId(null)} /> : null}
	</>;
};

const NewCount: React.FC<{ campusId: number; warehouses: any[]; rows: CountSummary[]; onOpen: (id: number) => void; onDone: (count: CountDetail) => void; onCancel: () => void }> = ({ campusId, warehouses, rows, onOpen, onDone, onCancel }) => {
	const physical = warehouses.filter((warehouse) => warehouse.kind !== "virtual");
	const [warehouseId, setWarehouseId] = useState<string>(String(physical[0]?.id || ""));
	// Opened before the warehouses arrived: pick the first one when they do (the select showed it but the button stayed off).
	useEffect(() => { if (warehouseId === "" && physical.length > 0) setWarehouseId(String(physical[0].id)); }, [physical.length]);
	const [kind, setKind] = useState<string>("initial");
	const [shelves, setShelves] = useState<string>("");
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	// La bodega ya tiene un conteo sin aplicar: se ofrece entrar a ése en vez de dejarlas atoradas.
	const openFolio = error ? (error.match(/\((CN-\d+)\)/) || [])[1] : undefined;
	const openCount = openFolio ? rows.find((row) => row.folio === openFolio) : undefined;
	const save = async () => {
		setBusy(true);
		setError(null);
		try {
			const response = await HttpManager.getInstance().stockOpsService.createCount(campusId, { warehouseId, kind, shelves: shelves.trim() });
			onDone(response.data);
		} catch (requestError: any) {
			setError(apiError(requestError, "No se pudo abrir el conteo."));
		} finally {
			setBusy(false);
		}
	};
	return <Modal width="520px" busy={busy} onClose={onCancel}>
		<h4 style={{ marginBottom: "12px" }}>Nuevo Conteo</h4>
		<div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
			<div style={ui.field("100%")}><span style={ui.caption}>Bodega</span>
				<select className="form-select" style={ui.input} value={warehouseId} disabled={busy} onChange={(event) => setWarehouseId(event.target.value)}>
					{physical.map((warehouse) => <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>)}
				</select>
				{physical.length < 1 ? <span style={{ ...ui.small, color: "var(--wa-oro)" }}>No se cargaron las bodegas: cierra y vuelve a abrir Bodegas.</span> : null}</div>
			<div style={{ display: "flex", gap: "6px" }}>
				<button type="button" className={"btn btn-sm rounded-pill " + (kind === "initial" ? "btn-primary" : "btn-outline-secondary")} disabled={busy} onClick={() => setKind("initial")}>Conteo Inicial</button>
				<button type="button" className={"btn btn-sm rounded-pill " + (kind === "cycle" ? "btn-primary" : "btn-outline-secondary")} disabled={busy} onClick={() => setKind("cycle")}>Conteo de Revisión</button>
			</div>
			{/* A count is always of the whole warehouse: applying it sets each product's total there (no counting by zones). */}
			<div style={ui.field("100%")}><span style={ui.caption}>Notas del Conteo (opcional)</span>
				<input type="text" className="form-control" style={ui.input} maxLength={255} value={shelves} disabled={busy} onChange={(event) => setShelves(event.target.value)} />
				<span style={ui.small}>Se cuenta toda la bodega.</span></div>
		</div>
		{error ? <div className="alert alert-danger" style={{ marginTop: "10px", padding: "8px 12px", fontSize: "14px" }}>{error}</div> : null}
		{openCount ? <button type="button" className="btn btn-primary rounded-pill" style={{ width: "100%", marginTop: "8px" }} onClick={() => onOpen(openCount.id)}>Entrar a {openCount.folio}</button> : null}
		<div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "14px" }}>
			<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={onCancel}>Cancelar</button>
			<button type="button" className="btn btn-primary rounded-pill" disabled={busy || warehouseId === ""} onClick={save}>{busy ? "Abriendo…" : "Empezar a Contar"}</button>
		</div>
	</Modal>;
};

/** base = lo que el servidor tenía la última vez que esta pantalla se sincronizó (null = aún no está en el servidor). */
interface EditLine { articleId: number; name: string; waCode: string | null; barcode: string | null; serialized: boolean; quantity: string; serials: string; base: number | null; serialsDirty?: boolean; serialsServer?: string }
/** Una celda mandada al servidor: si la respuesta no llega se repite tal cual (mismo opId), y el servidor no la suma dos veces. */
interface CellOp { opId: string; delta: number; sentQty: number; remove: boolean; serials?: string[]; serialsBase?: string[] }

const newOpId = () => Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 12);
const serialList = (text: string) => text.split(/[\s,;]+/).filter(Boolean);
/** Lo que esta pantalla tiene escrito y el servidor todavía no. */
const isDirty = (line: EditLine) => line.quantity !== "" && (line.base === null || (Number(line.quantity) || 0) !== line.base || !!line.serialsDirty);
/** Con avisos al instante la recarga es solo respaldo; sin ellos, cada 6 s. */
const pollMs = () => (push.conectado ? 30000 : 6000);

const CountModal: React.FC<{ campusId: number; countId: number; canCount: boolean; onChanged: () => void; onClose: () => void }> = ({ campusId, countId, canCount, onChanged, onClose }) => {
	const http = HttpManager.getInstance().stockOpsService;
	const [count, setCount] = useState<CountDetail | null>(null);
	const [lines, setLines] = useState<EditLine[]>([]);
	const [photos, setPhotos] = useState<File[]>([]);
	const [shelf, setShelf] = useState<string>("");
	const [signer, setSigner] = useState<string>("");
	const [second, setSecond] = useState<Record<number, string>>({});
	const [costs, setCosts] = useState<Record<number, string>>({});
	const [scan, setScan] = useState<string>("");
	const [scanNote, setScanNote] = useState<string | null>(null);
	const [cancelling, setCancelling] = useState<boolean>(false);
	const [teamPick, setTeamPick] = useState<ProductRow | null>(null);
	const [teamQty, setTeamQty] = useState<string>("1");
	const [scanning, setScanning] = useState<boolean>(false);
	const [scanNoCode, setScanNoCode] = useState<boolean>(false);
	const [resolving, setResolving] = useState<CountPending | null>(null);
	const [sizeTypes, setSizeTypes] = useState<any>({});
	const [colorSuggestions, setColorSuggestions] = useState<string[]>([]);
	// Producto con tallas/colores elegido en un buscador: se cuentan todas de una vez.
	const [countingModel, setCountingModel] = useState<number | null>(null);
	const [currency, setCurrency] = useState<string>("");
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [view, setView] = useState<"count" | "history">("count");
	const scanRef = useRef<HTMLInputElement>(null);

	/*
		Como hoja de cálculo (Esteban, 2026-10-30): cada celda se guarda sola unos instantes después de escribirla y la tabla trae
		cada pocos segundos lo que contaron los demás. Cada quien manda solo lo que cambió (su número − el que veía), así dos o
		más personas cuentan el mismo conteo a la vez sin pisarse; lo que alguien tiene escrito y aún no se guarda se respeta.
	*/
	// La tabla vive en linesRef (se lee al instante al mandar una celda) y se refleja en el estado para pintarse.
	const linesRef = useRef<EditLine[]>([]);
	const mutate = (change: (current: EditLine[]) => EditLine[]) => {
		linesRef.current = change(linesRef.current);
		setLines(linesRef.current);
	};
	const countRef = useRef<CountDetail | null>(null);
	// Con el escáner abierto, el escáner ya trae cada lectura: la tabla no se recarga por su cuenta.
	const scanningRef = useRef<boolean>(false);
	// La celda donde alguien está escribiendo: nunca se le cambia el número bajo los dedos.
	const focusedRef = useRef<number | null>(null);
	const inflight = useRef<Map<number, CellOp>>(new Map());
	const failed = useRef<Map<number, CellOp>>(new Map());
	const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
	// Sube con cada celda que sale o vuelve: una recarga que empezó antes se descarta (traería números a medio guardar).
	const epoch = useRef<number>(0);
	const alive = useRef<boolean>(true);
	const [sync, setSync] = useState<{ saving: number; offline: boolean; at: Date | null }>({ saving: 0, offline: false, at: null });
	const [cellError, setCellError] = useState<string | null>(null);

	const toEdit = (line: CountDetail["lines"][number]): EditLine => ({ articleId: line.articleId, name: line.name, waCode: line.waCode, barcode: line.barcode,
		serialized: line.serialized, quantity: String(line.counted), serials: (line.serials || []).join(", "), base: line.counted, serialsServer: (line.serials || []).join(", ") });
	const editing = (articleId: number) => timers.current.has(articleId) || focusedRef.current === articleId;
	/** Un renglón del servidor encima del de esta pantalla, sin perder lo que tiene escrito y aún no se guarda. */
	const mergeLine = (local: EditLine | undefined, server: CountDetail["lines"][number]): EditLine => {
		if (!local) return toEdit(server);
		// Su celda va en camino, se está reintentando o la están escribiendo: la respuesta de esa celda la pone al día.
		if (inflight.current.has(server.articleId) || failed.current.has(server.articleId) || editing(server.articleId)) return local;
		if (!isDirty(local)) return { ...toEdit(server), quantity: local.quantity === "" ? "" : String(server.counted) };
		// Lo suyo (lo escrito − lo que veía) encima de lo que hay hoy en el servidor.
		return { ...local, name: server.name, base: server.counted, quantity: String(Math.max(0, server.counted + (Number(local.quantity) || 0) - (local.base ?? 0))) };
	};
	/** Mete lo del servidor en la tabla sin perder lo que esta pantalla tiene escrito y aún no se guarda. */
	const apply = (data: CountDetail) => {
		countRef.current = data;
		setCount(data);
		mutate((current) => {
			const mine = new Map(current.map((line) => [line.articleId, line]));
			const merged = data.lines.map((server) => {
				const local = mine.get(server.articleId);
				mine.delete(server.articleId);
				return mergeLine(local, server);
			});
			// Agregados en esta pantalla que aún no llegan al servidor se quedan, y también un número escrito y sin guardar aunque
			// otra persona haya quitado el renglón (vuelve como nuevo); los demás que el servidor quitó (dados de baja) se van.
			mine.forEach((local) => { if (local.base === null || isDirty(local)) merged.push({ ...local, base: null }); });
			return merged;
		});
		setSync((current) => ({ ...current, at: new Date() }));
	};
	const load = useCallback(() => {
		http.count(campusId, countId).then((response) => apply(response.data)).catch((requestError) => setError(apiError(requestError, "No se pudo abrir el conteo.")));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId, countId]);
	useEffect(() => { load(); }, [load]);
	/** Trae lo que contaron los demás; si mientras tanto salió o volvió una celda, se descarta y se intenta en la siguiente vuelta. */
	const refresh = () => {
		if (inflight.current.size > 0) return Promise.resolve();
		const started = epoch.current;
		return http.count(campusId, countId).then((response) => {
			if (!alive.current || started !== epoch.current || inflight.current.size > 0) return;
			apply(response.data);
			setSync((current) => current.offline ? { ...current, offline: false } : current);
		}).catch(() => setSync((current) => ({ ...current, offline: true })));
	};
	const reload = () => { refresh(); };

	const finishOp = () => {
		epoch.current += 1;
		setSync((current) => ({ ...current, saving: inflight.current.size }));
	};
	/** Manda la celda de un artículo (una a la vez por artículo); al volver, si siguieron escribiendo, manda lo que falta. */
	const sendCell = (articleId: number, removal?: CellOp) => {
		if (inflight.current.has(articleId)) return;
		const line = linesRef.current.find((entry) => entry.articleId === articleId);
		let op = removal || failed.current.get(articleId);
		if (!op) {
			if (!line || !isDirty(line)) return;
			const quantity = Number(line.quantity) || 0;
			op = { opId: newOpId(), delta: quantity - (line.base ?? 0), sentQty: quantity, remove: false,
				serials: line.serialized ? serialList(line.serials) : undefined, serialsBase: line.serialized ? serialList(line.serialsServer || "") : undefined };
		}
		const sent = op;
		inflight.current.set(articleId, sent);
		epoch.current += 1;
		setSync((current) => ({ ...current, saving: inflight.current.size }));
		http.countCell(campusId, countId, { articleId, delta: sent.delta, remove: sent.remove || undefined, serials: sent.serials, serialsBase: sent.serialsBase, opId: sent.opId }).then((response) => {
			inflight.current.delete(articleId);
			failed.current.delete(articleId);
			if (!alive.current) return;
			const server = response.data.line;
			mutate((current) => {
				const local = current.find((entry) => entry.articleId === articleId);
				if (sent.remove || !local) return current;
				if (server === null) return current.filter((entry) => entry.articleId !== articleId);
				const sameSerials = !sent.serials || sent.serials.join(",") === serialList(local.serials).join(",");
				const serverSerials = (server.serials || []).join(", ");
				const serialsNext = { serialsServer: serverSerials, serialsDirty: !sameSerials, serials: sameSerials && local.serialized ? serverSerials : local.serials };
				if (editing(articleId)) {
					// Sigue escribiendo: su número no se toca; lo que escriba de más sale en la siguiente celda.
					return current.map((entry) => entry.articleId === articleId ? { ...entry, ...serialsNext, serials: local.serials, base: sent.sentQty } : entry);
				}
				const extra = local.quantity === "" ? 0 : (Number(local.quantity) || 0) - sent.sentQty;
				return current.map((entry) => entry.articleId === articleId
					? { ...entry, ...serialsNext, name: server.name, base: server.counted, quantity: String(Math.max(0, server.counted + extra)) } : entry);
			});
			setSync((current) => ({ ...current, offline: false, at: new Date() }));
			finishOp();
			// Lo que escribieron mientras viajaba esta celda sale en la siguiente.
			setTimeout(() => { if (alive.current) sendCell(articleId); }, 0);
		}).catch((requestError: any) => {
			inflight.current.delete(articleId);
			if (!alive.current) return;
			const status = requestError?.response?.status;
			if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
				// El servidor no la aceptó (producto dado de baja, conteo cerrado…): se avisa y la tabla vuelve a lo que hay.
				failed.current.delete(articleId);
				setCellError(apiError(requestError, "No se pudo guardar."));
				mutate((current) => current.map((entry) => entry.articleId === articleId && entry.base !== null ? { ...entry, quantity: String(entry.base), serialsDirty: false } : entry)
					.filter((entry) => entry.articleId !== articleId || entry.base !== null));
				finishOp();
				refresh();
				return;
			}
			// Sin respuesta: se repite la misma celda (mismo opId) hasta que llegue.
			failed.current.set(articleId, sent);
			setSync((current) => ({ ...current, offline: true }));
			finishOp();
			queueCell(articleId, 3000);
		});
	};
	const queueCell = (articleId: number, wait = 700) => {
		const timer = timers.current.get(articleId);
		if (timer) clearTimeout(timer);
		timers.current.set(articleId, setTimeout(() => { timers.current.delete(articleId); sendCell(articleId); }, wait));
	};
	/** Antes de escanear, mandar a firma o cerrar: todo lo escrito tiene que estar en el servidor. */
	const flush = async (): Promise<void> => {
		const deadline = Date.now() + 20000;
		focusedRef.current = null;
		mutate((current) => current.some((line) => line.quantity === "") ? current.map((line) => line.quantity === "" ? { ...line, quantity: "0" } : line) : current);
		for (;;) {
			timers.current.forEach((timer) => clearTimeout(timer));
			timers.current.clear();
			linesRef.current.filter(isDirty).forEach((line) => sendCell(line.articleId));
			failed.current.forEach((_, articleId) => sendCell(articleId));
			if (inflight.current.size === 0 && failed.current.size === 0 && !linesRef.current.some(isDirty)) return;
			if (Date.now() > deadline) throw new Error("Hay números que no se han podido guardar (¿sin internet?). Espera a que diga «Guardado» y vuelve a intentar.");
			await new Promise((resolve) => setTimeout(resolve, 150));
		}
	};
	useEffect(() => {
		alive.current = true;
		let last = Date.now();
		const tick = () => {
			if (document.visibilityState !== "visible" || scanningRef.current || countRef.current?.statusId !== S.COUNTING || Date.now() - last < pollMs()) return;
			last = Date.now();
			refresh();
		};
		const timer = setInterval(tick, 2000);
		const onVisible = () => { if (document.visibilityState === "visible") { last = 0; tick(); } };
		document.addEventListener("visibilitychange", onVisible);
		// Al instante: cada cambio de otra persona (o de esta misma en otro aparato) llega con sus renglones.
		let soon: ReturnType<typeof setTimeout> | null = null;
		const reloadSoon = () => {
			if (soon) clearTimeout(soon);
			// Con una celda propia en camino se espera a que vuelva (la recarga se descartaría).
			soon = setTimeout(() => { soon = null; if (inflight.current.size > 0) { reloadSoon(); return; } last = Date.now(); refresh(); }, 400);
		};
		const soltar = push.usar(campusId);
		const quitar = push.on("conteo", (data: any) => {
			if (!alive.current || Number(data?.c) !== countId) return;
			if (data.full || !Array.isArray(data.lines) || (countRef.current && data.s !== countRef.current.statusId)) { reloadSoon(); return; }
			mutate((current) => {
				let next = current;
				data.lines.forEach((server: any) => {
					const local = next.find((entry) => entry.articleId === server.articleId);
					if (server.gone) {
						// Lo quitaron del conteo: se va, salvo un número escrito aquí y sin guardar (vuelve como nuevo).
						if (!local || inflight.current.has(server.articleId) || failed.current.has(server.articleId)) return;
						next = local.base === null || isDirty(local) ? next.map((entry) => entry === local ? { ...entry, base: null } : entry) : next.filter((entry) => entry !== local);
						return;
					}
					const merged = mergeLine(local, server);
					next = local ? next.map((entry) => entry === local ? merged : entry) : [...next, merged];
				});
				return next;
			});
			setSync((current) => ({ ...current, at: new Date() }));
		});
		const pending = timers.current;
		return () => {
			alive.current = false;
			clearInterval(timer);
			if (soon) clearTimeout(soon);
			document.removeEventListener("visibilitychange", onVisible);
			quitar();
			soltar();
			pending.forEach((entry) => clearTimeout(entry));
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId, countId]);
	const editQuantity = (articleId: number, value: string) => {
		mutate((current) => current.map((entry) => entry.articleId === articleId ? { ...entry, quantity: value.replace("-", "") } : entry));
		queueCell(articleId);
	};
	/** Celda vacía al salir = 0 (se contó y no hay); al salir se guarda sin esperar. */
	const leaveCell = (articleId: number) => {
		if (focusedRef.current === articleId) focusedRef.current = null;
		mutate((current) => current.map((entry) => entry.articleId === articleId && entry.quantity === "" ? { ...entry, quantity: "0" } : entry));
		queueCell(articleId, 0);
	};
	const addLine = (line: Omit<EditLine, "base">) => {
		// Si ya estaba (lo trajo una recarga o un escaneo de hace un instante), se suma.
		mutate((current) => current.some((entry) => entry.articleId === line.articleId)
			? current.map((entry) => entry.articleId === line.articleId ? { ...entry, quantity: String((Number(entry.quantity) || 0) + (Number(line.quantity) || 0)) } : entry)
			: [...current, { ...line, base: null }]);
		queueCell(line.articleId, 0);
	};
	/** Quitar el producto del conteo es como borrar la fila de la hoja: se va lo que contaron todos. */
	const removeLine = (line: EditLine) => {
		if (line.base !== null && line.base > 0 && !window.confirm("¿Quitar «" + line.name + "» del conteo?\n\nSe borran las " + line.base + " piezas que lleva contadas (de todas las personas). Si solo quieres corregir, cambia el número.")) return;
		mutate((current) => current.filter((entry) => entry.articleId !== line.articleId));
		const timer = timers.current.get(line.articleId);
		if (timer) clearTimeout(timer);
		timers.current.delete(line.articleId);
		failed.current.delete(line.articleId);
		if (focusedRef.current === line.articleId) focusedRef.current = null;
		if (line.base !== null) sendCell(line.articleId, { opId: newOpId(), delta: 0, sentQty: 0, remove: true });
	};
	const closeModal = async () => {
		if (count && count.canEdit && canCount) {
			setBusy(true);
			try { await flush(); } catch (flushError: any) { setBusy(false); if (!window.confirm(flushError.message + "\n\n¿Cerrar de todos modos y perder lo que no se guardó?")) return; }
			setBusy(false);
		}
		onClose();
	};

	const run = async (call: () => Promise<any>): Promise<boolean> => {
		setBusy(true);
		setError(null);
		try {
			const response = await call();
			apply(response.data);
			onChanged();
			return true;
		} catch (requestError: any) {
			setError(apiError(requestError, "No se pudo completar."));
			return false;
		} finally {
			setBusy(false);
		}
	};

	// El lector escribe el código y Enter: suma una pieza (o agrega el producto si no estaba).
	const onScan = async () => {
		const code = scan.trim();
		setScan("");
		if (!code) return;
		const same = (line: EditLine) => (line.barcode || "").toLowerCase() === code.toLowerCase() || (line.waCode || "").toLowerCase() === code.toLowerCase();
		// A barcode can be shared by several products: never add to the first one without asking.
		if (lines.filter(same).length > 1) { setScanNote("\"" + code + "\" lo tienen varios productos de la tabla: súmalo a mano en el que es."); return; }
		const found = lines.findIndex(same);
		if (found >= 0) {
			const articleId = lines[found].articleId;
			mutate((current) => current.map((line) => line.articleId === articleId ? { ...line, quantity: String((Number(line.quantity) || 0) + 1) } : line));
			queueCell(articleId, 400);
			setScanNote(lines[found].name + ": " + ((Number(lines[found].quantity) || 0) + 1));
			return;
		}
		try {
			const response = await HttpManager.getInstance().inventoryService.list(campusId, { q: code, kinds: ["article", "vitrina"], includeVariants: 1 }, 1, 5);
			const match = (response.data.data || []).filter((product: any) => product.trackStock && ((product.barcode || "").toLowerCase() === code.toLowerCase() || (product.waCode || "").toLowerCase() === code.toLowerCase()));
			if (match.length > 1) { setScanNote("\"" + code + "\" lo tienen varios productos: agrégalo con «Agregar producto»."); return; }
			if (match.length !== 1) { setScanNote("\"" + code + "\" no se encontró."); return; }
			const product = match[0];
			addLine({ articleId: product.id, name: product.name, waCode: product.waCode, barcode: product.barcode, serialized: !!product.serialized, quantity: "1", serials: "" });
			setScanNote(product.name + ": 1");
		} catch {
			setScanNote("No se pudo buscar el código.");
		}
	};

	if (!count) return <Modal onClose={onClose}>{error ? <div className="alert alert-danger">{error}</div> : <Loading />}</Modal>;
	const st = count.statusId;
	/** flush() con su motivo en el aviso de error de run(). */
	const flushOrThrow = async () => {
		try { await flush(); } catch (flushError: any) { throw { message: flushError.message, response: { data: { message: flushError.message } } }; }
	};
	/** Fotos del estante (los números ya se guardan solos al escribirlos). */
	const saveFiles = async () => {
		if (photos.length < 1) return;
		const response = await http.saveCountFiles(campusId, count.id, photos, shelf.trim());
		setPhotos([]);
		apply(response.data);
	};
	// El escáner guarda cada pieza en el servidor: antes de abrirlo, lo escrito en la tabla termina de guardarse.
	const openScanner = async (noCode: boolean) => {
		setBusy(true);
		setError(null);
		try {
			await flush();
			onChanged();
			setScanNoCode(noCode);
			setScanning(true);
		} catch (requestError: any) {
			setError(requestError?.response ? apiError(requestError, "No se pudo guardar antes de escanear.") : requestError.message);
		} finally {
			setBusy(false);
		}
	};
	scanningRef.current = scanning;
	const syncText = sync.offline ? "Sin conexión: se reintenta solo…" : sync.saving > 0 || lines.some(isDirty) ? "Guardando…"
		: "Guardado" + (sync.at ? " · al día " + sync.at.toLocaleTimeString("es-MX", { hour: "numeric", minute: "2-digit", second: "2-digit" }) : "");
	const loadCatalogs = () => {
		if (Object.keys(sizeTypes).length > 0) return;
		HttpManager.getInstance().inventoryService.catalogs(campusId).then((response) => {
			setSizeTypes(response.data.sizeTypes || {});
			setColorSuggestions(response.data.colorSuggestions || []);
			setCurrency(currencyOf(response.data.countryCode));
		}).catch(() => setSizeTypes({}));
	};
	/** Un producto con tallas/colores (o una de sus tallas) abre la cuadrícula de todo el producto. */
	const pickedModel = (product: any): number | null => product.variantCount > 0 ? product.id : product.modelId ? product.modelId : null;
	const countByModel = (modelId: number) => { loadCatalogs(); setCountingModel(modelId); };
	const openResolve = (row: CountPending) => {
		if (Object.keys(sizeTypes).length < 1) HttpManager.getInstance().inventoryService.catalogs(campusId).then((response) => {
			setSizeTypes(response.data.sizeTypes || {});
			setColorSuggestions(response.data.colorSuggestions || []);
			setCurrency(currencyOf(response.data.countryCode));
		}).catch(() => setSizeTypes({}));
		setResolving(row);
	};
	// Solo cambia la lista de "por dar de alta": lo que esté escrito en la tabla de renglones sin guardar se respeta.
	const renamePending = async (row: CountPending, name: string): Promise<boolean> => {
		setBusy(true);
		setError(null);
		try {
			const response = await http.scanPendingRename(campusId, count.id, row.id, name, true);
			setCount((current) => current ? { ...current, pending: response.data.pending, scanners: response.data.scanners } : current);
			onChanged();
			return true;
		} catch (requestError: any) {
			setError(apiError(requestError, "No se pudo cambiar el nombre."));
			return false;
		} finally {
			setBusy(false);
		}
	};
	const setPendingQuantity = async (row: CountPending, quantity: number): Promise<boolean> => {
		setBusy(true);
		setError(null);
		try {
			const response = await http.scanPendingQuantity(campusId, count.id, row.id, quantity, true);
			setCount((current) => current ? { ...current, pending: response.data.pending, scanners: response.data.scanners } : current);
			onChanged();
			return true;
		} catch (requestError: any) {
			setError(apiError(requestError, "No se pudo cambiar la cantidad."));
			return false;
		} finally {
			setBusy(false);
		}
	};
	const pendingPhoto = (row: CountPending, extra: number | null = null) => http.scanPendingPhoto(campusId, count.id, row.id, false, extra)
		.then((response) => openBlob(response.data, row.name + (extra === null ? "" : " " + (extra + 2)))).catch(() => setError("No se pudo abrir la foto."));

	return <Modal width="900px" busy={busy} onClose={closeModal}>
		<div style={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "flex-start" }}>
			<div>
				<h4 style={{ margin: 0, fontFamily: "monospace" }}>{count.folio} {chip(st, count.status)}</h4>
				<div style={{ ...ui.small, marginTop: "4px" }}>{count.kindLabel} · {count.warehouse}{count.shelves ? " · " + count.shelves : ""} · contó {count.countedBy}
					{count.secondCounter ? " · firma " + count.secondCounter : ""}{count.approvedBy ? " · aplicó " + count.approvedBy + " " + count.approvedAt : ""}</div>
			</div>
			<button type="button" className="btn-close" onClick={closeModal} disabled={busy} />
		</div>
		<div style={{ ...ui.tabs, margin: "12px 0 0" }}>
			{([["count", "Conteo"], ["history", "Historial"]] as const).map(([key, text]) => <button key={key} type="button" disabled={busy}
				className={"btn btn-sm rounded-pill " + (view === key ? "btn-primary" : "btn-outline-secondary")} onClick={() => setView(key)}>{text}</button>)}
		</div>
		{view === "history" ? <HistorialConteo campusId={campusId} countId={count.id} /> : <>

		{!count.canEdit && count.canScan && canCount ? <>
			<button type="button" className="btn btn-primary rounded-pill" style={{ marginTop: "12px", width: "100%", height: "44px", fontSize: "15px" }} disabled={busy}
				onClick={() => { setScanNoCode(false); setScanning(true); }}>Escanear con la Cámara</button>
			<button type="button" className="btn btn-outline-secondary rounded-pill" style={{ marginTop: "8px", width: "100%", height: "44px", fontSize: "15px" }} disabled={busy}
				onClick={() => { setScanNoCode(true); setScanning(true); }}>Producto Nuevo Sin Código</button>
			{/* En equipo también se busca por nombre: lo elegido se suma a lo que ya contaron los demás. */}
			<div style={{ display: "flex", gap: "8px", alignItems: "flex-end", flexWrap: "wrap", marginTop: "10px" }}>
				<div style={{ ...ui.field("260px"), flex: "1 1 260px" }}><span style={ui.caption}>Buscar producto por nombre</span>
					{teamPick ? <div style={{ ...ui.input, display: "flex", alignItems: "center", justifyContent: "space-between", gap: "6px", padding: "0 10px", borderRadius: "6px" }}>
						<span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{teamPick.name}{teamPick.sizeValue && !teamPick.name.includes(teamPick.sizeValue) ? " · " + teamPick.sizeValue : ""}</span>
						<button type="button" className="btn-close" style={{ fontSize: "10px" }} disabled={busy} onClick={() => setTeamPick(null)} /></div>
						: <ProductPicker campusId={campusId} kinds={["article", "vitrina"]} groupVariants disabled={busy} hideStock placeholder="Nombre o código WA…" onPick={(product) => { const modelId = pickedModel(product); if (modelId) { countByModel(modelId); return; } setTeamPick(product); setTeamQty("1"); }} />}</div>
				<div style={ui.field("90px")}><span style={ui.caption}>Piezas</span>
					<input type="number" min={1} className="form-control" style={ui.input} value={teamQty} disabled={busy || !teamPick} onChange={(event) => setTeamQty(event.target.value)} /></div>
				<button type="button" className="btn btn-primary rounded-pill" disabled={busy || !teamPick || !(Number(teamQty) > 0)}
					onClick={() => { const product = teamPick; if (!product) return; run(() => http.scanAdd(campusId, count.id, product.id, Math.floor(Number(teamQty)), null, false)).then((ok) => { if (ok) setTeamPick(null); }); }}>Sumar</button>
			</div>
		</> : null}
		{count.scanners.length > 0 ? <div style={{ ...ui.small, marginTop: "8px" }}>Escanearon: {count.scanners.map((person) => person.name + " (" + person.pieces + ")").join(" · ")}</div> : null}
		{count.canEdit && canCount ? <>
			<button type="button" className="btn btn-primary rounded-pill" style={{ marginTop: "12px", width: "100%", height: "44px", fontSize: "15px" }} disabled={busy} onClick={() => openScanner(false)}>Escanear con la Cámara</button>
			<button type="button" className="btn btn-outline-secondary rounded-pill" style={{ marginTop: "8px", width: "100%", height: "44px", fontSize: "15px" }} disabled={busy} onClick={() => openScanner(true)}>Producto Nuevo Sin Código</button>
			<form style={{ ...ui.field("100%"), marginTop: "12px" }} onSubmit={(event) => { event.preventDefault(); onScan(); }}>
				<span style={ui.caption}>Escanear</span>
				<input ref={scanRef} type="text" className="form-control" style={ui.input} value={scan} disabled={busy} placeholder="Código de barras o código WA" onChange={(event) => setScan(event.target.value)} />
				{scanNote ? <span style={ui.small}>{scanNote}</span> : null}
			</form>
			<div style={{ ...ui.tiny, marginTop: "8px", textAlign: "right", color: sync.offline ? "var(--wa-acento-texto)" : "var(--wa-apagado)" }}>{syncText}</div>
			{cellError ? <div className="alert alert-warning" style={{ marginTop: "6px", padding: "6px 10px" }}>{cellError} <button type="button" className="btn-close" style={{ fontSize: "10px", float: "right" }} onClick={() => setCellError(null)} /></div> : null}
			<table style={{ width: "100%", borderCollapse: "collapse", marginTop: "6px" }}>
				<thead><tr><th style={ui.th}>Producto</th><th style={{ ...ui.th, width: "110px" }}>Contadas</th><th style={{ ...ui.th, width: "30px" }}></th></tr></thead>
				<tbody>{lines.map((line) => <tr key={line.articleId}>
					<td style={ui.td}><div style={{ fontWeight: 600 }}>{line.name}</div><div style={ui.tiny}>{line.waCode}</div>
						{line.serialized ? <input type="text" className="form-control" style={{ ...ui.input, marginTop: "4px" }} placeholder="Números de serie separados por coma" value={line.serials} disabled={busy}
							onChange={(event) => { const value = event.target.value; mutate((current) => current.map((entry) => entry.articleId === line.articleId ? { ...entry, serials: value, serialsDirty: true } : entry)); queueCell(line.articleId, 1200); }}
							onFocus={() => { focusedRef.current = line.articleId; }} onBlur={() => leaveCell(line.articleId)} /> : null}</td>
					<td style={ui.td}><input type="number" min={0} inputMode="numeric" className="form-control" style={ui.input} value={line.quantity} disabled={busy}
						onFocus={() => { focusedRef.current = line.articleId; }} onBlur={() => leaveCell(line.articleId)}
						onChange={(event) => editQuantity(line.articleId, event.target.value)} /></td>
					<td style={ui.td}><button type="button" className="btn btn-sm btn-link" style={{ color: "var(--wa-apagado)", padding: 0 }} disabled={busy || sync.saving > 0} title="Quitar este producto del conteo" onClick={() => removeLine(line)}>✕</button></td>
				</tr>)}</tbody>
			</table>
			<div style={{ marginTop: "6px" }}>
				<ProductPicker campusId={campusId} kinds={["article", "vitrina"]} groupVariants excludeIds={lines.map((line) => line.articleId)} disabled={busy} hideStock placeholder="Agregar producto…"
					onPick={(product: any) => { const modelId = pickedModel(product); if (modelId) { countByModel(modelId); return; } if (!product.trackStock) { setError("\"" + product.name + "\" no se cuenta así."); return; } addLine({ articleId: product.id, name: product.name, waCode: product.waCode, barcode: product.barcode, serialized: !!product.serialized, quantity: "1", serials: "" }); }} />
			</div>
			<div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "10px", alignItems: "flex-end" }}>
				<div style={ui.field("160px")}><span style={ui.caption}>Estante de estas fotos</span>
					<input type="text" className="form-control" style={ui.input} maxLength={60} value={shelf} disabled={busy} onChange={(event) => setShelf(event.target.value)} /></div>
				<PhotoInput caption="Fotos del estante" photos={photos} busy={busy} onChange={setPhotos} />
				<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={() => run(async () => { await flushOrThrow(); await saveFiles(); return http.count(campusId, count.id); })}>{busy ? "Guardando…" : "Guardar Avance"}</button>
			</div>
			{count.canSubmit ? <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "12px", alignItems: "flex-end", borderTop: "1px solid var(--wa-filo)", paddingTop: "10px" }}>
				<div style={ui.field("260px")}><span style={ui.caption}>Quién firma (otra persona)</span>
					<select className="form-select" style={ui.input} value={signer} disabled={busy} onChange={(event) => setSigner(event.target.value)}>
						<option value="">Elige…</option>
						{count.counters.filter((person) => person.id !== count.countedById && !count.scanners.some((scanner) => scanner.userId === person.id)).map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}
					</select></div>
				<button type="button" className="btn btn-primary rounded-pill" disabled={busy || signer === "" || lines.length < 1}
					onClick={() => {
						if (!window.confirm("¿Ya terminaron de contar TODA la bodega?\n\nAl mandarlo a firma ya no se escanea. Si les falta, toquen Cancelar: el avance ya queda guardado y pueden volver a entrar desde la lista.")) return;
						run(async () => {
							await flushOrThrow();
							await saveFiles();
							onChanged();
							return http.submitCount(campusId, count.id, Number(signer));
						});
					}}>Mandar a Firma</button>
				<div style={{ ...ui.tiny, flexBasis: "100%" }}>Solo al terminar toda la bodega. Cada escaneo ya queda guardado: pueden cerrar y volver a entrar con "Seguir Contando" en la lista.</div>
			</div> : <div style={{ ...ui.tiny, marginTop: "12px", borderTop: "1px solid var(--wa-filo)", paddingTop: "10px" }}>Lo manda a firma {count.countedBy || "quien abrió el conteo"}.</div>}
		</> : <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "12px" }}>
			<thead><tr>
				<th style={ui.th}>Producto</th>
				<th style={{ ...ui.th, ...ui.right }}>Contó</th>
				{st === S.TO_SIGN && count.canSign ? <th style={{ ...ui.th, width: "120px" }}>Mi conteo</th> : <th style={{ ...ui.th, ...ui.right }}>Firma</th>}
				{count.lines.some((line) => line.system !== null) ? <><th style={{ ...ui.th, ...ui.right }} title="Existencia cuando se contó: ya descuenta lo que se entregó, compró o trasladó después">Sistema al contar</th><th style={{ ...ui.th, ...ui.right }}>Diferencia</th></> : null}
				{count.canApprove ? <th style={{ ...ui.th, width: "130px" }}>Costo estimado</th> : count.lines.some((line) => line.unitCost !== null) ? <th style={{ ...ui.th, ...ui.right }}>Costo</th> : null}
			</tr></thead>
			<tbody>{count.lines.map((line) => <tr key={line.id}>
				<td style={ui.td}><div style={{ fontWeight: 600 }}>{line.name}</div><div style={ui.tiny}>{line.waCode}{line.serials.length ? " · " + line.serials.join(", ") : ""}</div></td>
				<td style={{ ...ui.td, ...ui.right }}>{line.counted}</td>
				{st === S.TO_SIGN && count.canSign
					? <td style={ui.td}><input type="number" min={0} className="form-control" style={ui.input} placeholder={String(line.counted)} value={second[line.id] ?? ""} disabled={busy} onChange={(event) => setSecond({ ...second, [line.id]: event.target.value })} /></td>
					: <td style={{ ...ui.td, ...ui.right, color: line.second !== null ? "var(--wa-oro)" : undefined, fontWeight: line.second !== null ? 700 : 400 }}>{line.second !== null ? line.second : (st >= S.TO_APPROVE ? "✓" : "-")}</td>}
				{count.lines.some((entry) => entry.system !== null) ? <>
					<td style={{ ...ui.td, ...ui.right }}>{line.system ?? "-"}</td>
					<td style={{ ...ui.td, ...ui.right, fontWeight: 700, color: (line.difference || 0) < 0 ? "var(--wa-oro)" : undefined }}>{line.difference === null ? "-" : (line.difference > 0 ? "+" : "") + line.difference}</td>
				</> : null}
				{count.canApprove ? <td style={ui.td}><input type="number" min={0} step="0.01" className="form-control" style={ui.input} value={costs[line.id] ?? (line.unitCost ?? "")} disabled={busy}
					onChange={(event) => setCosts({ ...costs, [line.id]: event.target.value })} /></td>
					: count.lines.some((entry) => entry.unitCost !== null) ? <td style={{ ...ui.td, ...ui.right }}>{line.unitCost ?? "-"}</td> : null}
			</tr>)}</tbody>
		</table>}

		{count.canSign ? <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "10px" }}>
			<button type="button" className="btn btn-primary rounded-pill" disabled={busy}
				onClick={() => run(() => http.signCount(campusId, count.id, Object.entries(second).filter(([, value]) => value !== "").map(([lineId, value]) => ({ lineId: Number(lineId), quantity: Number(value) }))))}>Firmar conteo</button>
		</div> : null}
		{count.canReopen ? <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "10px" }}>
			<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy}
				onClick={() => { if (window.confirm("¿Regresar " + count.folio + " a Contando?\n\nPodrán seguir escaneando; al terminar se vuelve a mandar a firma.")) run(() => http.reopenCount(campusId, count.id)); }}>Seguir Contando</button>
		</div> : null}
		{count.canApprove ? <div style={{ ...ui.small, marginTop: "6px" }}>"Sistema al contar" es la existencia que había cuando se contó cada producto: lo que se entregó, compró o trasladó después no se pierde al aplicar.</div> : null}
		{count.canApprove ? <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "10px" }}>
			<button type="button" className="btn btn-primary rounded-pill" disabled={busy}
				onClick={() => run(() => http.applyCount(campusId, count.id, Object.entries(costs).filter(([, value]) => value !== "").map(([lineId, value]) => ({ lineId: Number(lineId), unitCost: Number(value) }))))}>Aplicar al inventario</button>
		</div> : null}

		{count.pending.length > 0 ? <div style={{ marginTop: "12px", border: "1px solid var(--wa-oro)", borderRadius: "10px", overflow: "hidden" }}>
			<div style={{ padding: "8px 12px", fontWeight: 700, color: "var(--wa-oro)", background: "var(--wa-oro-suave)" }}>Por Dar de Alta ({pendingGroups(count.pending).length})</div>
			{pendingGroups(count.pending).map((group) => { const { first: row, pieces, label } = group; return <div key={row.id} style={{ display: "flex", gap: "10px", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", padding: "8px 12px", borderTop: "1px solid var(--wa-filo)" }}>
				<div style={{ minWidth: 0, flex: "1 1 220px" }}>
					<NombrePorDarDeAlta row={row} label={label} extra={row.batchKey || !count.canScan ? <span style={{ fontWeight: 700 }}> × {pieces}</span> : null} canEdit={!!count.canScan} disabled={busy} onSave={renamePending} />
					<div style={ui.tiny}>{row.barcode || "sin código"}{row.brand ? " · " + row.brand : ""} · escaneó {row.createdBy}{row.suggestedPrice ? " · precio sugerido " + row.suggestedPrice.toFixed(2) : ""}</div>
				</div>
				{/* wrap: on a phone, quantity + Foto + Dar de Alta + Descartar did not fit and Descartar was cut off the right edge. */}
				<div style={{ display: "flex", gap: "6px", flex: "0 1 auto", alignItems: "center", flexWrap: "wrap", minWidth: 0 }}>
					{count.canScan && !row.batchKey ? <CantidadesPorDarDeAlta group={group} disabled={busy} onSave={setPendingQuantity} /> : null}
					{row.hasPhoto ? <button type="button" className="btn btn-outline-secondary btn-sm rounded-pill" disabled={busy} onClick={() => pendingPhoto(row)}>Foto</button> : null}
					{Array.from({ length: row.extraPhotos || 0 }, (_, index) => <button key={index} type="button" className="btn btn-outline-secondary btn-sm rounded-pill" disabled={busy}
						onClick={() => pendingPhoto(row, index)}>Foto {index + 2}</button>)}
					{count.canCreateProducts ? <button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={busy} onClick={() => openResolve(row)}>Dar de Alta</button> : null}
					{count.canCreateProducts || count.canEdit || (count.canScan && row.createdById != null && row.createdById === count.me) ? <button type="button" className="btn btn-link btn-sm waOroTexto" disabled={busy} onClick={() => run(() => http.scanPendingDiscard(campusId, count.id, row.id))}>Descartar</button> : null}
				</div>
				{count.canScan && row.batchKey ? <CantidadesPorDarDeAlta group={group} disabled={busy} onSave={setPendingQuantity} /> : null}
			</div>; })}
		</div> : null}

		{count.files.length > 0 ? <div style={{ marginTop: "12px" }}>
			<div style={{ fontWeight: 700, fontSize: "14px" }}>Fotos</div>
			{count.files.map((file, index) => <button key={file.id} type="button" className="btn btn-link btn-sm" style={{ padding: "0 8px 0 0" }}
				onClick={() => http.countFile(campusId, count.id, file.id).then((response) => openBlob(response.data, (file.shelf || "Foto") + " " + (index + 1))).catch(() => setError("No se pudo abrir la foto."))}>{file.shelf || "Foto"} {index + 1}</button>)}
		</div> : null}
		{error ? <div className="alert alert-danger" style={{ marginTop: "10px", padding: "8px 12px", fontSize: "14px" }}>{error}</div> : null}
		{st < S.APPLIED && count.canCancel ? <div style={{ marginTop: "12px" }}><button type="button" className="btn btn-link btn-sm waOroTexto" style={{ padding: 0 }} disabled={busy} onClick={() => setCancelling(true)}>Cancelar Conteo</button></div> : null}
		</>}
		{/* apply (not setCount): the editable table follows every scan, so "Guardar avance" right after closing never erases them. */}
		{scanning ? <Escaner campusId={campusId} count={count} startNoCode={scanNoCode} onCount={apply} onClose={() => { setScanning(false); reload(); onChanged(); }} /> : null}
		{countingModel !== null ? <ContarPorTallas campusId={campusId} count={count} modelId={countingModel} catalogs={{ sizeTypes, colorSuggestions }} canCreate={!!count.canCreateProducts}
			onCancel={() => setCountingModel(null)} onDone={(data) => { setCountingModel(null); apply(data); onChanged(); }} /> : null}
		{resolving ? <NuevoProducto campusId={campusId} countId={count.id} code={null} lookup={null} pending={resolving} canCreate={true} sizeTypes={sizeTypes} currency={currency}
			colorSuggestions={colorSuggestions} batch={resolving.batchKey ? count.pending.filter((row) => row.batchKey === resolving.batchKey) : undefined}
			onCancel={() => setResolving(null)} onDone={(data) => { setResolving(null); apply(data); onChanged(); }} /> : null}
		{cancelling ? <ReasonPrompt title={"Cancelar " + count.folio} label="Motivo" confirmLabel="Cancelar Conteo" confirmClass="btn-primary" busy={busy} error={error}
			onCancel={() => setCancelling(false)} onConfirm={(reason) => { setCancelling(false); run(() => http.cancelCount(campusId, count.id, reason)); }} /> : null}
	</Modal>;
};

export default Conteos;
